import sitesClient from '@/core/api/sites-service';
import { AutocompleteDTO, SiteListItemDTO } from '@/../client-generator/generated-clients/sites';
import { Vue, Component, Watch } from 'vue-property-decorator';
import PieChart from '@/views/components/ChartsComponents/PieChart';
import kiosksClient from '@/core/api/kiosks-service';
import { KioskDTO, KiosksTotalStatsDTO } from '@/../client-generator/generated-clients/kiosks';
import { ChartData, ChartDataSets } from 'chart.js';
import { LOADING_CONTENT } from '@/core/store/types/action-types';
import i18n from '@/core/translations/i18n';

enum KioskVotesColors {
  one = '#DB5653',
  two = '#E69E60',
  three = '#EADA7D',
  four = '#96B58F',
}

@Component({
  components: {
    PieChart,
  },
})
export default class KiosksStatistics extends Vue {
  chartLoading = false;
  KioskVotesColors = KioskVotesColors;

  sites: AutocompleteDTO[] = [
    {
      id: 'ALL',
      value: i18n.t('allSites'),
    } as AutocompleteDTO,
  ];

  places: KioskDTO[] = [
    {
      kioskId: -1,
      kioskName: i18n.t('allPlaces'),
    } as KioskDTO,
  ];

  dateFilter = null;
  siteSelected = 'ALL';
  placeSelected = -1;

  kioskStatistics: KiosksTotalStatsDTO | null = null;

  chartData: ChartData = {
    datasets: [],
    labels: [],
  };

  @Watch('dateFilter')
  fromDateUpdated() {
    this.fetchKioskStatistics();
  }

  @Watch('siteSelected')
  fromSiteUpdated() {
    this.placeSelected = -1;
    this.chartLoading = true;

    this.$nextTick(() => {
      this.fetchPlaces();
    });
  }

  @Watch('placeSelected')
  placeUpdated(newPlace) {
    this.chartLoading = true;

    if (newPlace !== -1) {
      this.$nextTick(() => {
        this.fetchKioskStatistics();
      });
    }
  }

  get labelSiteSelected() {
    const site = this.sites.find((s) => s.id === this.siteSelected);
    return site ? site.value : '';
  }

  get isPlaceSelectorEnabled() {
    return this.siteSelected !== 'ALL' && this.places && this.places.length > 0;
  }

  async mounted() {
    await this.fetchSitesList();
    this.fetchKioskStatistics();
  }

  async fetchSitesList() {
    this.$store.dispatch(LOADING_CONTENT, true);

    try {
      const response = await sitesClient.myList('');

      this.sites = (response.data as SiteListItemDTO[]).map(
        (site) =>
          ({
            id: site.siteCode ?? '',
            value: site.siteName ?? '',
          } as AutocompleteDTO)
      );

      this.sites.unshift({
        id: 'ALL',
        value: 'Tutte le ubicazioni',
      } as AutocompleteDTO);
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 204:
          messageError = this.$t('customerNotActive').toString();
          break;
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('noCustomerFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchKioskStatistics() {
    this.chartLoading = true;
    this.$store.dispatch(LOADING_CONTENT, true);

    try {
      this.kioskStatistics = await kiosksClient.myStatistics(
        this.dateFilter ?? undefined,
        this.siteSelected === 'ALL' ? undefined : this.siteSelected,
        this.placeSelected === -1 ? undefined : this.placeSelected
      );

      this.$nextTick(() => {
        this.initChart();
      });
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 400:
          messageError = this.$t('apiErrors.badRequest').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('noCustomerFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchPlaces() {
    if (this.siteSelected === 'ALL') return;

    this.$store.dispatch(LOADING_CONTENT, true);

    try {
      this.places = await kiosksClient.myKiosks(this.siteSelected);

      if (this.placeSelected === -1) {
        this.$nextTick(() => {
          this.fetchKioskStatistics();
        });
      }
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 400:
          messageError = this.$t('apiErrors.badRequest').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('noCustomerFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  initChart() {
    if (!this.kioskStatistics || !this.kioskStatistics.votes) return;

    const dataSets = [
      {
        backgroundColor: [KioskVotesColors.one, KioskVotesColors.two, KioskVotesColors.three, KioskVotesColors.four],
        data: [
          this.kioskStatistics.votes[1] ?? 0,
          this.kioskStatistics.votes[2] ?? 0,
          this.kioskStatistics.votes[3] ?? 0,
          this.kioskStatistics.votes[4] ?? 0,
        ],
      },
    ] as ChartDataSets[];

    this.chartData.datasets = dataSets;
    this.chartData.labels = [
      this.$t('votes.1').toString(),
      this.$t('votes.2').toString(),
      this.$t('votes.3').toString(),
      this.$t('votes.4').toString(),
    ];

    this.chartLoading = false;
  }

  getVotesToDisplay(votes) {
    const votesToDisplay = votes ?? 0;
    return votesToDisplay;
  }

  calcVotesPercent(votes) {
    const finalVotes = votes ?? 0;

    if (finalVotes === 0) {
      return '0%';
    }

    const votesTotal = (this.kioskStatistics as KiosksTotalStatsDTO).votesTotal ?? 1;

    return `${((finalVotes * 100) / votesTotal).toFixed(2)}%`;
  }
}
